import {Injectable} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {Observable} from "rxjs";
import {Result} from "@domain/common/http/result";
import {environment} from "@environments/environment";
import {first,map} from "rxjs/operators";
import {Genre,Prestation,TypePrestation} from "@domain/prestation/prestation";
import {Referentiel} from "@domain/admin/referentiels/referentiel";
import {handleResult} from "@share/utils/handle-result";
import {TranslateService} from "@ngx-translate/core";
import {ToastrService} from "ngx-toastr";
import {DisplayCompte} from "@domain/comptabilite/compte";
import {Field} from "@domain/settings/field";
import {Unite} from "@domain/prestation/unite";
import {DisplayCumul} from "@domain/prestation/prest-cumul";
import {Domaine} from "@domain/taxe/domaine";
import {ZoneUtilisateur} from "@domain/zone-utilisateur/zone-utilisateur";
import {Bareme} from "@domain/prestation/bareme";
import {TypeBareme} from "@domain/prestation/TypeBareme";
import {PuissanceFiscale} from "@domain/admin/entreprise/vehicules/puissanceFiscale";
import {PrestFiltre} from "@domain/prestation/prest-filtre";
import * as moment from "moment";
import {Montant} from "@domain/prestation/montant";

/**
 * Service de gestion des prestations
 */
@Injectable()
export class PrestationService {
	/** Liste des types de prestation */
	readonly listeTypes: Array<{type: TypePrestation,libelle: string}> = [
		{type: TypePrestation.REEL,libelle: this.translateService.instant("admin.frais.prestations.detail.reglesGestion.remboursement.type.reel") },
		{type: TypePrestation.PLAFOND,libelle: this.translateService.instant("admin.frais.prestations.detail.reglesGestion.remboursement.type.plafond") },
		{type: TypePrestation.FORFAIT,libelle: this.translateService.instant("admin.frais.prestations.detail.reglesGestion.remboursement.type.forfait") },
	];

	/** Liste des barèmes */
	readonly listeBaremes: Array<{id: TypeBareme,libelle: string}> = [
		{ id: TypeBareme.SANS,libelle: this.translateService.instant("admin.frais.prestations.detail.reglesGestion.remboursement.bareme.sans") },
		{ id: TypeBareme.BAREME_FISCAL,libelle: this.translateService.instant("admin.frais.prestations.detail.reglesGestion.remboursement.bareme.fiscal") },
		{ id: TypeBareme.BAREME_SNCF,libelle: this.translateService.instant("admin.frais.prestations.detail.reglesGestion.remboursement.bareme.sncf") }
	];

	/**
	 * Constructeur
	 */
	constructor(private http: HttpClient,private translateService: TranslateService,private toastrService: ToastrService) {}

	/**
	 * Chargement d'une prestation
	 *
	 * @param idPrestation Identifiant de la prestation
	 */
	load(idPrestation: string): Observable<Prestation> {
		return this.http.post<Result>(`${environment.baseUrl}/controller/AdminPrestation/load/${idPrestation}`,null)
						.pipe(map(result => {
							const prestation = new Prestation(result.data.prestation);
							if (result.data.prestation.genre == "") { prestation.genre = Genre.STANDARD; }
							return prestation;
						}));
	}

	/**
	 * Sauvegarde d'une prestation
	 *
	 * @param prestation Prestation à sauvegarder
	 */
	save(prestation: Prestation): Observable<Result> {
		return this.http.post<Result>(`${environment.baseUrl}/controller/AdminPrestation/save`,prestation).pipe(first());
	}

	/**
	 * Suppression d'une prestation
	 *
	 * @param idPrestation Identifiant de la prestation
	 */
	delete(idPrestation: string): Observable<Result> {
		return this.http.delete<Result>(`${environment.baseUrl}/controller/AdminPrestation/delete/${idPrestation}`).pipe(first());
	}

	/**
	 * Duplication d'une prestation
	 *
	 * @param idPrestation Identifiant de la prestation
	 */
	duplicate(idPrestation: string): Observable<Result> {
		return this.http.post<Result>(`${environment.baseUrl}/controller/AdminPrestation/duplicate/${idPrestation}`,null).pipe(first());
	}

	/**
	 * Chargement des familles
	 */
	listeFamille(): Observable<Referentiel[]> {
		return this.http.post<Result>(`${environment.baseUrl}/controller/AdminPrestation/listeFamille`,null)
						.pipe(map(result => result.data.listeFamille.map(famille => {
							const referentiel = famille as Referentiel;
							referentiel.id = famille.idReferentiel;
							return referentiel;
						}) as Referentiel[]));
	}

	/**
	 * Chargement des types de prestations
	 */
	listeTypePrestation(): Observable<Referentiel[]> {
		return this.http.post<Result>(`${environment.baseUrl}/controller/AdminPrestation/listeTypePrestation`,null)
						.pipe(map(result => result.data.listeTypePrestation.map(type => {
							const referentiel = type as Referentiel;
							referentiel.id = type.idReferentiel;
							return referentiel;
						}) as Referentiel[]));
	}

	/**
	 * Chargement des comptes charge
	 */
	listeCompteCharge(): Observable<DisplayCompte[]> {
		return this.http.post<Result>(`${environment.baseUrl}/controller/AdminPrestation/listeCompteCharge`,null)
						.pipe(map(result => result.data.listeCompteCharge.map(compte => new DisplayCompte(compte)) as DisplayCompte[]));
	}

	/**
	 * Chargement des codes liasses
	 */
	listeCodeLiasse(): Observable<Referentiel[]> {
		return this.http.post<Result>(`${environment.baseUrl}/controller/AdminPrestation/listeCodeLiasse`,null)
						.pipe(map(result => result.data.listeCodeLiasse.map(code => {
							const referentiel = code as Referentiel;
							referentiel.id = code.idReferentiel;
							return referentiel;
						}) as Referentiel[]));
	}

	/**
	 * Chargement des zones utilisateurs prédéfinies
	 */
	listeZoneUtilisateurPredefinie(): Observable<Field[]> {
		return this.http.post<Result>(`${environment.baseUrl}/controller/AdminPrestation/listeZoneUtilisateurPredefinie`,null)
						.pipe(map(result => result.data.listeZoneUtilisateurPredefinie?.map(field => {
							field.masque = field.masque || '';
							field.libelle = field.libelle || field.champ;
							return field;
						}) as Field[]));
	}

	/**
	 * Chargement des unités
	 */
	listeUnite(): Observable<Unite[]> {
		return this.http.post<Result>(`${environment.baseUrl}/controller/AdminPrestation/listeUnite`,null)
						.pipe(map(result => result.data.listeUnite as Unite[]));
	}

	/**
	 * Chargement des domaines prestations
	 */
	listeDomainePrestation(): Observable<Referentiel[]> {
		return this.http.post<Result>(`${environment.baseUrl}/controller/AdminPrestation/listeDomainePrestation`,null)
						.pipe(map(result => result.data.listeDomainePrestation.map(domaine => {
							const referentiel = domaine as Referentiel;
							referentiel.id = domaine.idReferentiel;
							return referentiel;
						}) as Referentiel[]));
	}

	/**
	 * Chargement des cumuls
	 */
	listeCumul(): Observable<DisplayCumul[]> {
		return this.http.post<Result>(`${environment.baseUrl}/controller/AdminPrestation/listeCumul`,null)
						.pipe(map(result => result.data.listeCumul as DisplayCumul[]));
	}

	/**
	 * Chargement des comptes externes
	 */
	listeCompteExterne(): Observable<DisplayCompte[]> {
		return this.http.post<Result>(`${environment.baseUrl}/controller/AdminPrestation/listeCompteExterne`,null)
						.pipe(map(result => result.data.listeCompteExterne.map(compte => new DisplayCompte(compte)) as DisplayCompte[]));
	}

	/**
	 * Chargement des domaines taxe
	 */
	listeDomaineTaxe(): Observable<Domaine[]> {
		return this.http.post<Result>(`${environment.baseUrl}/controller/AdminPrestation/listeDomaineTaxe`,null)
						.pipe(map(result => result.data.listeDomaineTaxe as Domaine[]));
	}

	/**
	 * Suppression d'une zone utilisateur
	 */
	deleteLienZuPrest(idLienZuPrest: number): Observable<Result> {
		return this.http.delete<Result>(`${environment.baseUrl}/controller/AdminPrestation/deleteLienZuPrest/${idLienZuPrest}`);
	}

	/**
	 * Chargement des ZU possibles
	 */
	listeZoneUtilisateurPossible(idPrestation: number): Observable<ZoneUtilisateur[]> {
		return this.http.post<Result>(`${environment.baseUrl}/controller/AdminPrestation/listeZoneUtilisateurPossible/${idPrestation}`,null)
						.pipe(map(result => result.data.listeZoneUtilisateurPossible as ZoneUtilisateur[]));
	}

	/**
	 * Création d'un nouveau lien ZU Prestation
	 *
	 * @param idPrestation Id de la prestation concernée
	 * @param zoneUtilisateur Zone utilisateur à associer
	 */
	saveLienZuPrest(idPrestation: number,zoneUtilisateur: ZoneUtilisateur): Observable<Result> {
		return this.http.post<Result>(`${environment.baseUrl}/controller/AdminPrestation/saveLienZuPrest/${idPrestation}`,zoneUtilisateur)
						.pipe(handleResult(this.translateService,this.toastrService,'global.success.enregistrement','global.errors.generic'));
	}

	/**
	 * Sauvegarde d'un barème
	 *
	 * @param baremeToSave barème à sauvegarder
	 * @param typeBareme type du barème
	 */
	saveBareme(baremeToSave: Bareme,typeBareme: TypeBareme): Observable<Result> {
		return this.http.post<Result>(`${environment.baseUrl}/controller/AdminPrestation/saveBareme${typeBareme == TypeBareme.BAREME_SNCF ? 'SNCF' : '' }`,baremeToSave);
	}

	/**
	 * Suppression d'un barème
	 *
	 * @param idBareme ID du barème à supprimer
	 * @param typeBareme type du barème
	 */
	deleteBareme(idBareme: number,typeBareme: TypeBareme): Observable<Result> {
		return this.http.delete<Result>(`${environment.baseUrl}/controller/AdminPrestation/deleteBareme${typeBareme == TypeBareme.BAREME_SNCF ? 'SNCF' : '' }/${idBareme}`);
	}

	/**
	 * Création d'une map pour retrouver le dernier barème par date
	 *
	 * @param listeBareme liste des barèmes à parcourir
	 */
	mapLastBaremeByDate(listeBareme: Bareme[]): Map<string,number> {
		//Initialisation d'une map pour retrouver le dernier barème par date
		const mapLastBaremeByDate: Map<string,number> = new Map<string,number>();

		//Parcours de la liste des barèmes
		listeBareme?.forEach(bareme => {
			//Rend moment.js exploitable depuis les TUs
			const m = (moment as any).default ?? (moment as any);

			//Conversion de la date avec moment.js
			const dateAppliMoment: string = m(bareme.dateAppli).toDate().toString();

			//Récupération du barème trouvé
			const baremeFound: number = mapLastBaremeByDate.get(dateAppliMoment);

			//Si le barème trouvé est plus petit que le barème actuel
			if (!baremeFound || bareme.maxi > baremeFound || bareme.maxi == 0) {
				//Mise à jour de la map
				mapLastBaremeByDate.set(dateAppliMoment,(bareme.maxi == 0 ? bareme.mini : bareme.maxi));
			}
		});

		//Retour de la map
		return mapLastBaremeByDate;
	}

    /**
     * Chargement des pays possibles
     */
    listePays(): Observable<Result> {
        return this.http.post<Result>(`${environment.baseUrl}/controller/AdminPrestation/listePays`,null);
    }

    /**
     * Chargement des puissances fiscales possibles
     */
    listePuissanceFiscale(): Observable<PuissanceFiscale[]> {
        return this.http.post<Result>(`${environment.baseUrl}/controller/AdminPrestation/listePuissanceFiscale`,null)
            			.pipe(map(result => result.data.listePuissanceFiscale as PuissanceFiscale[]));
    }

    /**
     * Sauvegarde d'un filtre sur une prestation
     *
     * @param idPrestation ID de la prestation concernée
     * @param filtre filtre à sauvegarder
     */
    savePrestationFiltre(idPrestation: number, filtre: PrestFiltre): Observable<Result> {
        return this.http.post<Result>(`${environment.baseUrl}/controller/AdminPrestation/savePrestationFiltre/${idPrestation}`,filtre).pipe(first());
    }

    /**
     * Suppression d'un filtre sur une prestation
     *
     * @param idPrestation ID de la prestation concernée
     * @param filtre filtre à supprimer
     */
    deletePrestationFiltre(idPrestation: string, filtre: PrestFiltre): Observable<Result> {
        return this.http.post<Result>(`${environment.baseUrl}/controller/AdminPrestation/deletePrestationFiltre/${idPrestation}`,filtre).pipe(first());
    }

	/**
	 * Sauvegarde d'un montant
	 *
	 * @param montant montant à sauvegarder
	 */
	saveMontant(montant: Montant): Observable<Result> {
		return this.http.post<Result>(`${environment.baseUrl}/controller/AdminPrestation/saveMontant`,montant).pipe(first());
	}

	/**
	 * Suppression d'un montant
	 *
	 * @param montant montant à supprimer
	 */
	deleteMontant(montant: Montant): Observable<Result> {
		return this.http.post<Result>(`${environment.baseUrl}/controller/AdminPrestation/deleteMontant`,montant).pipe(first());
	}
}
